import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { useAppContext, Product, ModelType } from "../../context/AppContext";
import { useBrand } from "../../context/BrandContext";
import { useCopy } from "../../context/CopyContext";
import ApiService from "../../services/apiService";
import { useDetectionWebSocket } from "../../hooks/useWebSocket";
import Header from "../Header";
import Heading from "../Heading";
import Button from "../Button";
import MultiLineCopy from "../MultiLineCopy";
import PoweredBy from "../PoweredBy";
import ItemsModalView from "./ItemsModalView";

const CheckingEmptyView: React.FC = () => {
  const navigate = useNavigate();
  const { state, dispatch } = useAppContext();
  const { brandConfig, isDarkMode } = useBrand();
  const { getCopy } = useCopy();
  const [showItemsModal, setShowItemsModal] = useState(false);
  const [cameraFrame, setCameraFrame] = useState<string>('');
  const [cameraError, setCameraError] = useState<string>('');
  const [detectionError, setDetectionError] = useState<string>('');
  const [products, setProducts] = useState<Product[]>([]);
  const frameIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const detectionStartedRef = useRef(false);
  
  // WebSocket for real-time detection
  const { 
    isConnected: wsConnected, 
    detectionStatus, 
    detectionResult, 
    startDetection 
  } = useDetectionWebSocket(state.settings.selectedModel);

  // Reset detection flag on component mount
  useEffect(() => {
    detectionStartedRef.current = false;
    console.log('🔄 Component mounted, reset detection flag');
  }, []);

  // Load products from catalog based on selected model
  useEffect(() => {
    const loadProducts = async () => {
      try {
        // Load demo products for Demo model, otherwise load full catalog
        const productsFile = state.settings.selectedModel === ModelType.DEMO 
          ? '/demo-products.json' 
          : '/mock-products.json';
        
        const response = await fetch(productsFile);
        const productsData = await response.json();
        setProducts(productsData);
        
        console.log(`📦 Loaded products from ${productsFile}:`, productsData.length, 'products');
      } catch (error) {
        console.error('Failed to load products:', error);
      }
    };
    loadProducts();
  }, [state.settings.selectedModel]);

  const handleCancel = () => {
    dispatch({ type: "SET_STEP", payload: 1 });
    navigate("/add-empty");
  };

  const handleItemsClick = () => {
    setShowItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setShowItemsModal(false);
  };

  // Fetch camera frames continuously
  useEffect(() => {
    const fetchCameraFrame = async () => {
      try {
        const frameData = await ApiService.getCameraPreview();
        if (frameData.frame) {
          setCameraFrame(frameData.frame);
          setCameraError('');
        }
      } catch (error) {
        console.error('Failed to fetch camera frame:', error);
        setCameraError('Unable to access camera');
      }
    };

    // Start fetching frames
    fetchCameraFrame();
    frameIntervalRef.current = setInterval(fetchCameraFrame, 100); // 10 FPS

    return () => {
      if (frameIntervalRef.current) {
        clearInterval(frameIntervalRef.current);
      }
    };
  }, []);

  // Function to find matching product in catalog
  const findMatchingProduct = (detectionResult: any): Product | null => {
    if (!detectionResult || !products.length) return null;
    
    const detectedName = detectionResult.product_name?.toLowerCase() || '';
    const detectedSize = detectionResult.volume?.toLowerCase() || detectionResult.size?.toLowerCase() || '';
    
    // Try to match by cleaning up the detected name
    // Remove file extension if present
    const cleanedName = detectedName.replace(/\.(jpg|jpeg|png|webp)$/i, '').trim();
    
    console.log(`🔍 Trying to match: "${cleanedName}" (size: "${detectedSize}")`);
    
    // Special handling for Kiehl's products - always map to 125ml
    if (cleanedName.includes('kiehls') || cleanedName.includes("kiehl's") || 
        cleanedName.includes('ultra facial cream') || cleanedName.includes('ultra facial creme')) {
      console.log('🎯 Detected Kiehl\'s product - mapping to 125ml version');
      return products.find(p => p.id === 'kiehls-ultra-facial-cream-125ml') || null;
    }
    
    // Look for product that matches both name AND size
    const matchedProduct = products.find(product => {
      const productName = product.name.toLowerCase();
      const productBrand = product.brand?.toLowerCase() || '';
      const productSize = product.size.toLowerCase();
      
      // Match by name and/or brand
      const nameMatches = cleanedName.includes(productName) || 
                         productName.includes(cleanedName) ||
                         (productBrand && cleanedName.includes(productBrand)) ||
                         // Special case for variations
                         (cleanedName.includes('creme de corps') && productName.includes('creme de corps')) ||
                         // MAC products (brand-specific matching)
                         (cleanedName.includes('mac') && cleanedName.includes('mascara') && productBrand === 'mac') ||
                         (cleanedName.includes('mac') && cleanedName.includes('lipstick') && productBrand === 'mac') ||
                         // Other demo products
                         (cleanedName.includes('cerave') && productBrand === 'cerave') ||
                         (cleanedName.includes('nivea') && productBrand === 'nivea');
      
      // Match by size if we have it
      const sizeMatches = !detectedSize || productSize === detectedSize || 
                         productSize === detectedSize.replace(/\s/g, '');
      
      return nameMatches && sizeMatches;
    });
    
    return matchedProduct || null;
  };

  // Process detection results
  useEffect(() => {
    console.log('🔍 Detection result changed:', detectionResult);
    
    if (detectionResult && detectionResult.success) {
      console.log('✅ Detection successful! Product:', detectionResult.product_name);
      
      // Try to find matching product in catalog
      const matchedProduct = findMatchingProduct(detectionResult);
      
      if (matchedProduct) {
        console.log('✨ Found matching product in catalog:', matchedProduct);
        
        // Use the matched product from catalog
        const detectedProduct = {
          ...matchedProduct,
          detectionConfidence: detectionResult.confidence || 0,
          detectionMethod: detectionResult.detection_method || "unknown"
        };
        
        console.log('📦 Using catalog product:', detectedProduct);
        
        // Add detected product to context - PREVIEW it first
        dispatch({ type: "PREVIEW_PRODUCT", payload: detectedProduct });
        dispatch({ type: "SET_STEP", payload: 5 });
        
        console.log('🚀 Navigating to /empty-accepted with product:', detectedProduct);
        // Navigate with the product data directly to ensure it's shown
        navigate("/empty-accepted", { state: { detectedProduct } });
      } else {
        // Fallback: create product from detection result
        console.log('⚠️ No matching product found in catalog, using detection data');
        
        const detectedProduct = {
          id: detectionResult.barcode || `detected-${Date.now()}`,
          name: detectionResult.product_name || "Unknown Product",
          brand: detectionResult.brand || "Kiehl's",
          category: "skincare",
          size: detectionResult.volume || "Standard",
          price: 25.00,
          image: detectionResult.image || "/api/placeholder/200/200",
          rewardValue: detectionResult.rewardValue || 10,
          isBranded: true,
          rewards: {
            loyaltyPoints: 50,
            dollarValue: "$5.00",
            discountValue: "20%"
          },
          detectionConfidence: detectionResult.confidence || 0,
          detectionMethod: detectionResult.detection_method || "unknown"
        };
        
        console.log('📦 Created fallback product:', detectedProduct);
        
        // Only preview the product, don't add it to cart yet
        dispatch({ type: "PREVIEW_PRODUCT", payload: detectedProduct });
        dispatch({ type: "SET_STEP", payload: 5 });
        
        console.log('🚀 Navigating to /empty-accepted...');
        navigate("/empty-accepted");
      }
      
      // Show mock detection warning if using mock data
      if (detectionResult.using_mock) {
        console.warn("Using MOCK detection. For real detection, install dependencies.");
      }
    } else if (detectionResult && !detectionResult.success) {
      console.error('❌ Detection failed:', detectionResult.error);
      // Check if it's a "no item detected" scenario (HTTP 500 from empty scan)
      if (detectionResult.error && detectionResult.error.includes("HTTP Error 500")) {
        setDetectionError("No item detected. Please ensure your cosmetic's empty is placed in the scanning area. Need help? Speak to one of our experts.");
      } else {
        setDetectionError(detectionResult.error || "Unable to identify product. Please try again.");
      }
    } else {
      console.log('⏳ No detection result yet');
    }
  }, [detectionResult, products, dispatch, navigate]);

  // Use a timestamp to track when we first become ready
  const [readyTime, setReadyTime] = useState<number | null>(null);

  // Mark when we're ready for detection
  useEffect(() => {
    const isCameraReady = !!cameraFrame && !cameraError;
    const isWebSocketReady = wsConnected;
    const hasNotStartedDetection = !detectionStartedRef.current;
    const noErrors = !detectionError;
    
    const isReady = isCameraReady && isWebSocketReady && hasNotStartedDetection && noErrors;
    
    if (isReady && readyTime === null) {
      setReadyTime(Date.now());
    } else if (!isReady && readyTime !== null) {
      setReadyTime(null);
    }
  }, [cameraFrame, cameraError, wsConnected, detectionError, readyTime]);

  // Auto-trigger detection after 2 seconds of being ready
  useEffect(() => {
    if (readyTime === null) return;
    
    const timeElapsed = Date.now() - readyTime;
    const remainingTime = 2000 - timeElapsed;
    
    if (remainingTime <= 0 && !detectionStartedRef.current) {
      // Ready for 2+ seconds, trigger detection
      console.log('⏰ Timer fired!');
      console.log('🚀 Triggering detection now!');
      startDetection();
      detectionStartedRef.current = true;
      console.log('✅ Detection triggered successfully!');
      return;
    }
    
    // Set timer for remaining time
    console.log(`⏱️ Ready for ${timeElapsed}ms, waiting ${remainingTime}ms more...`);
    const timer = setTimeout(() => {
      if (!detectionStartedRef.current) {
        console.log('⏰ Timer fired!');
        console.log('🚀 Triggering detection now!');
        startDetection();
        detectionStartedRef.current = true;
        console.log('✅ Detection triggered successfully!');
      }
    }, remainingTime);
    
    return () => clearTimeout(timer);
  }, [readyTime, startDetection]);

  // Debug logging for ready conditions
  useEffect(() => {
    const isCameraReady = !!cameraFrame && !cameraError;
    const isWebSocketReady = wsConnected;
    const hasNotStartedDetection = !detectionStartedRef.current;
    const noErrors = !detectionError;
    
    if (isCameraReady && isWebSocketReady && hasNotStartedDetection && noErrors) {
      console.log('✅ All conditions met for detection!');
      console.log('- Camera ready:', isCameraReady);
      console.log('- WebSocket connected:', isWebSocketReady);
      console.log('- Detection not started:', hasNotStartedDetection);
      console.log('- No errors:', noErrors);
      
      if (readyTime) {
        console.log('📅 Marking ready time:', readyTime);
      }
    }
  }, [cameraFrame, cameraError, wsConnected, detectionError, readyTime]);

  return (
    <div
      className={`min-h-screen flex flex-col relative overflow-hidden ${
        isDarkMode
          ? "bg-gray-900"
          : "bg-gradient-to-b from-[#FFF3E2] to-[#FFE5CC]"
      }`}
    >
      <Header showItems={false} showSettings={true} />

      <div className="flex-1 flex flex-col items-center justify-center px-16 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-4xl mx-auto text-center space-y-12"
        >
          <Heading title={getCopy("checkingEmpty.heading")} />

          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="relative"
          >
            <div className="relative mx-auto w-[600px] h-[450px] rounded-3xl overflow-hidden shadow-2xl">
              {cameraError ? (
                <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                  <p className="text-gray-600">{cameraError}</p>
                </div>
              ) : cameraFrame ? (
                <img 
                  src={cameraFrame.startsWith('data:') ? cameraFrame : `data:image/jpeg;base64,${cameraFrame}`}
                  alt="Camera view"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full bg-gray-200 animate-pulse flex items-center justify-center">
                  <p className="text-gray-600">Loading camera...</p>
                </div>
              )}
              
              {/* Detection overlay */}
              <div className="absolute inset-0 pointer-events-none">
                <motion.div
                  animate={{
                    opacity: [0.3, 0.6, 0.3],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                  className="absolute inset-0 border-4 border-brand-primary rounded-3xl"
                />
              </div>
            </div>

            {/* Animated scanning line */}
            <motion.div
              className="absolute left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent"
              animate={{
                top: ["10%", "90%", "10%"],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              style={{
                filter: "blur(2px)",
                opacity: 0.8,
              }}
            />
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="space-y-4"
          >
            <p
              className={`text-2xl ${
                isDarkMode ? "text-gray-300" : "text-gray-700"
              }`}
            >
              {getCopy("checkingEmpty.subheading")}
            </p>
            {detectionError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`mt-6 p-4 rounded-lg ${
                  detectionError.includes("No item detected") 
                    ? "bg-gray-100 text-gray-700" 
                    : "bg-red-50 text-red-700"
                }`}
              >
                <p className="text-lg font-medium">
                  {detectionError}
                </p>
              </motion.div>
            )}
          </motion.div>
        </motion.div>
      </div>

      <div className="px-16 pb-16">
        <div className="flex flex-col items-center space-y-6">
          <MultiLineCopy
            text={getCopy("checkingEmpty.helpText")}
            className={`text-xl ${
              isDarkMode ? "text-gray-400" : "text-gray-600"
            }`}
          />
          <div className="flex gap-6">
            <Button
              variant="secondary"
              size="large"
              onClick={handleCancel}
              className="min-w-[200px]"
            >
              {getCopy("checkingEmpty.buttons.secondary")}
            </Button>
          </div>
        </div>
      </div>

      <PoweredBy />

      <ItemsModalView isOpen={showItemsModal} onClose={handleCloseItemsModal} />
    </div>
  );
};

export default CheckingEmptyView;